import React, { FC, useMemo } from 'react';
import { EntityId } from '@reduxjs/toolkit';
import { DialogPreview, DialogPreviewProps, DialogStateShape, dialogModel, useLastMessage, useParticipant } from '@cp/entities/dialog';
import { DialogMemberViewModel } from '@common/model/dialog/dialogMemberViewModel';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { User } from '@cp/entities/user';
import { useSelector } from 'react-redux';

export interface DialogListItemProps {
  currentUser: User;
  dialogId: EntityId;
  onSelect: (dialogId: number) => void;
  layout?: 'row';
}

export const DialogListItem: FC<DialogListItemProps> = ({ dialogId, onSelect, currentUser, layout }) => {
  const theme = useTheme();
  const currentMemberSelectorParams = useMemo(
    () => ({
      user: currentUser,
      dialogId,
    }),
    [dialogId, currentUser],
  );
  const participant = useParticipant(currentMemberSelectorParams);
  const unreadMessagesCount = useSelector<DialogStateShape, ReturnType<typeof dialogModel.selectors.unreadMessagesCountInDialogSelector>>(
    (state) => dialogModel.selectors.unreadMessagesCountInDialogSelector(state, currentMemberSelectorParams),
  );
  const lastMessage = useLastMessage(dialogId);

  const previewProps: Partial<DialogPreviewProps> = useMemo(() => {
    if (layout !== 'row') {
      return {};
    }
    return {
      sx: { background: '#fff', border: 'none', borderRadius: `${theme.borderRadius.normal}px`, p: 1.2, flex: '0 0 33.3%' },
      messageLines: 2,
    };
  }, [layout, theme]);

  return (
    <DialogPreview
      component="li"
      dialogId={dialogId as number}
      key={dialogId}
      message={lastMessage}
      onSelect={onSelect}
      participant={participant as DialogMemberViewModel}
      unreadMessagesCount={unreadMessagesCount}
      {...previewProps}
    />
  );
};
